/**
 * Converts a base64 data URI to a Blob, preserving its MIME type. Returns null
 * if the URI is malformed.
 *
 * @param dataUri A data URI, e.g. "data:image/png;base64,....".
 */
export function dataUriToBlob(dataUri: string): Blob | null {
    const commaIndex = dataUri.indexOf(",");
    if (commaIndex === -1) {
        return null;
    }

    const meta = dataUri.slice(0, commaIndex);
    const base64 = dataUri.slice(commaIndex + 1);
    const mimeMatch = meta.match(/data:(.*?);base64/);
    const mime =
        mimeMatch && mimeMatch[1] ? mimeMatch[1] : "application/octet-stream";

    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    return new Blob([bytes], { type: mime });
}

/**
 * Triggers a browser download of a data URI without going through AngularJS's
 * href sanitizer, which rewrites `data:` URIs to `unsafe:` and breaks the
 * download.
 *
 * @param dataUri A data URI, e.g. "data:image/png;base64,....".
 * @param filename The name to save the file as.
 */
export function downloadDataUri(dataUri: string, filename: string): void {
    const blob = dataUriToBlob(dataUri);
    if (!blob) {
        return;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
